
import React, { useState, useEffect } from 'react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { History, Search, Eye, Users, Calendar, CheckCircle, XCircle, Clock, AlertCircle, Mail, FileText, Check, AlertTriangle, ArrowDown, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface BulkEmail {
  id: string;
  subject: string;
  status: string;
  recipient_count: number;
  sent_at: string | null;
  created_at: string;
  error_message?: string;
  template_id?: string;
  email_templates?: {
    name: string;
  };
}

const StatusIndicator: React.FC<{ status: string }> = ({ status }) => {
  const s = status?.toLowerCase();
  if (s === 'sent' || s === 'completed') {
    return (
      <div
        className="w-7 h-7 rounded-full bg-green-50 border border-green-200 flex items-center justify-center text-green-600 shadow-sm"
        title={status}
      >
        <Check className="w-4 h-4 stroke-[3px]" />
      </div>
    );
  }
  if (s === 'failed' || s === 'error') {
    return (
      <div
        className="w-7 h-7 rounded-full bg-red-50 border border-red-200 flex items-center justify-center text-red-600 shadow-sm"
        title={status}
      >
        <AlertTriangle className="w-4 h-4" />
      </div>
    );
  }
  // sending, processing, pending
  return (
    <div
      className="w-7 h-7 rounded-full bg-orange-50 border border-orange-200 flex items-center justify-center text-orange-500 shadow-sm"
      title={status}
    >
      <Clock className="w-4 h-4" />
    </div>
  );
};

const formatDateAndTime = (dateString: string | null) => {
  if (!dateString) return { date: 'Not sent yet', time: '' };
  const date = new Date(dateString);

  // Format: 27/10/2025
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const dStr = `${day}/${month}/${year}`;

  // Format: 14:32
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const tStr = `${hours}:${minutes}`;

  return { date: dStr, time: tStr };
};

const EmailHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [bulkEmails, setBulkEmails] = useState<BulkEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchBulkEmails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- run on mount

  const fetchBulkEmails = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('bulk_emails')
        .select(`
          id,
          subject,
          status,
          recipient_count,
          sent_at,
          created_at,
          error_message,
          template_id,
          email_templates (
            name
          )
        `)
        .order('sent_at', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBulkEmails(data || []);
    } catch (error) {
      console.error('Error fetching bulk emails:', error);
      toast({
        title: "Error",
        description: "Failed to fetch email history. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredEmails = bulkEmails.filter(email => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      email.subject.toLowerCase().includes(searchLower) ||
      (email.email_templates?.name || '').toLowerCase().includes(searchLower);
    const matchesStatus = statusFilter === 'all' || email.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Reset selection on filter/search change
  useEffect(() => {
    setSelectedIds([]);
  }, [searchTerm, statusFilter]);

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === filteredEmails.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredEmails.map(email => email.id));
    }
  };

  const handleViewDetails = (emailId: string) => {
    navigate(`/email-center/history/${emailId}`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Filters Skeleton */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row gap-4">
            <Skeleton className="h-10 flex-1" />
            <Skeleton className="h-10 w-full sm:w-48" />
          </div>
        </div>

        {/* Table Skeleton */}
        <div className="p-6 space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="w-4 h-4 rounded" />
              <Skeleton className="w-7 h-7 rounded-full" />
              <Skeleton className="h-5 flex-1" />
              <Skeleton className="w-24 h-6 rounded-full" />
              <Skeleton className="w-28 h-5" />
              <Skeleton className="w-32 h-5" />
              <Skeleton className="w-8 h-8 rounded-lg" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Filters */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search emails by subject or template..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-gray-200 focus:border-purple-500 focus:ring-purple-500"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 border-gray-200 focus:border-purple-500 focus:ring-purple-500">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="sending">Sending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Email History Table */}
      {filteredEmails.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/50">
                <th className="py-4 px-6 w-12 text-center">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                    checked={filteredEmails.length > 0 && selectedIds.length === filteredEmails.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="py-4 px-4 font-semibold text-sm text-gray-500 w-24">Status</th>
                <th className="py-4 px-4 font-semibold text-sm text-gray-500">Subject</th>
                <th className="py-4 px-4 font-semibold text-sm text-gray-500">Template</th>
                <th className="py-4 px-4 font-semibold text-sm text-gray-500">Recipients</th>
                <th className="py-4 px-4 font-semibold text-sm text-gray-500 select-none">
                  <div className="flex items-center gap-1">
                    Date Sent
                    <ArrowDown className="w-3.5 h-3.5 text-gray-400" />
                  </div>
                </th>
                <th className="py-4 px-6 font-semibold text-sm text-gray-500 text-center w-20">View</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmails.map((email) => (
                <tr key={email.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/40 transition-colors">
                  <td className="py-4 px-6 text-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-purple-600 focus:ring-purple-500 w-4 h-4 cursor-pointer"
                      checked={selectedIds.includes(email.id)}
                      onChange={() => handleToggleSelect(email.id)}
                    />
                  </td>
                  <td className="py-4 px-4">
                    <StatusIndicator status={email.status} />
                  </td>
                  <td className="py-4 px-4 max-w-xs md:max-w-md">
                    <div className="font-normal text-gray-900 truncate">{email.subject}</div>
                    {email.error_message && (
                      <div className="text-xs text-red-600 mt-1 font-medium truncate flex items-center gap-1" title={email.error_message}>
                        <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                        <span>{email.error_message}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-4">
                    {email.email_templates?.name ? (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-[#8B5CF6] border border-purple-100 hover:bg-purple-100/70 transition-colors">
                        {email.email_templates.name}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{email.recipient_count} recipients</span>
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    {(() => {
                      const { date, time } = formatDateAndTime(email.sent_at);
                      return (
                        <div className="text-sm font-medium">
                          <span className="text-gray-950">{date}</span>
                          {time && <span className="text-gray-400 ml-1.5 font-normal">{time}</span>}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="py-4 px-6 text-center">
                    <button
                      onClick={() => handleViewDetails(email.id)}
                      className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-purple-600 transition-all duration-200 inline-flex items-center justify-center"
                      title="View details"
                    >
                      <Eye className="w-4.5 h-4.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50/50">
          <div className="p-4 bg-white rounded-full w-fit mx-auto mb-4 shadow-sm border border-gray-100">
            <History className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No bulk emails found</h3>
          <p className="text-gray-600">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : "You haven't sent any bulk emails yet"}
          </p>
        </div>
      )}
    </div>
  );
};

export default EmailHistory;
